import type { MouseEvent, ReactNode } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  Check,
  Circle,
  Clock,
  GraduationCap,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  AgentWorkflowRunTaskStatus,
  InboxItemAssignee,
  ONBOARDING_TASK_TYPE,
  PendingInboxItem,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import type { DataTableColumn } from "@/components/ui/data-table";
import { DataTableBadge } from "@/components/ui/data-table-badge";
import { getTaskActionDisplayName } from "@/modules/agents/components/helpers/richActionHelpers";
import { getItemTitle } from "@/pages/InboxItemRow";
import { getRowUpdatedAt, type InboxTableRow } from "@/pages/inboxTableRows";

/** Everything the inbox table's cells need beyond the row itself. */
export interface InboxTableColumnOptions {
  currentUserId?: string;
  showStatus: boolean;
  canCancel: (item: PendingInboxItem) => boolean;
  onCancel: (e: MouseEvent, item: PendingInboxItem) => void;
}

const KIND_LABEL_CLASS =
  "shrink-0 text-[10.5px] font-semibold uppercase tracking-wider";

/**
 * Builds the task inbox's table columns. Kept as a plain array so a caller can
 * later filter it down to a user's chosen columns.
 * @param options - Current user, whether the status column is shown, and the
 * cancel action wiring
 * @returns The ordered column definitions for the inbox table
 */
export function buildInboxTableColumns(
  options: InboxTableColumnOptions,
): DataTableColumn<InboxTableRow>[] {
  const { currentUserId, showStatus, canCancel, onCancel } = options;

  return [
    {
      key: "task",
      header: "Task",
      className: "px-3 py-2",
      format: (_value, row) => (
        <div className="flex flex-col gap-0.5">
          <span
            className="line-clamp-2 font-medium text-foreground"
            title={getRowTitle(row)}
          >
            {getRowTitle(row)}
          </span>
          <span className="line-clamp-1 flex items-baseline gap-2 text-xs text-muted-foreground">
            {renderTaskKind(row)}
            <span>{renderOwners(getRowAssignees(row), currentUserId)}</span>
          </span>
        </div>
      ),
    },
    {
      key: "opportunity",
      header: "Opportunity",
      width: "30%",
      className: "px-3 py-2",
      format: (_value, row) =>
        row.kind === "agent" ? renderOpportunity(row.item) : "-",
    },
    ...(showStatus
      ? [
          {
            key: "status",
            header: "Status",
            width: 140,
            className: "px-3 py-2",
            format: (_value, row) => renderStatus(row),
          } satisfies DataTableColumn<InboxTableRow>,
        ]
      : []),
    {
      key: "updated",
      header: "Updated",
      width: 140,
      align: "right",
      className: "px-3 py-2",
      format: (_value, row) => (
        <span
          className="text-xs tabular-nums text-muted-foreground"
          title={getRowUpdatedAt(row).toLocaleString()}
        >
          {formatDistanceToNow(getRowUpdatedAt(row), { addSuffix: true })}
        </span>
      ),
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      width: 64,
      align: "center",
      className: "px-2 py-1",
      format: (_value, row) => {
        if (row.kind !== "agent" || !canCancel(row.item)) {
          return null;
        }
        return (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 [@media(pointer:coarse)]:opacity-100"
            onClick={(e) => onCancel(e, row.item)}
            aria-label="Cancel task"
            title="Cancel task"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        );
      },
    },
  ];
}

/**
 * Renders an agent task's opportunity name and pipeline stage.
 * @param item - The inbox item
 * @returns The opportunity cell content
 */
function renderOpportunity(item: PendingInboxItem): ReactNode {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span
        className="line-clamp-2 break-words"
        title={item.opportunityName ?? undefined}
      >
        {item.opportunityName ?? "-"}
      </span>
      {item.stageName && (
        <span className="text-[10.5px] font-semibold uppercase tracking-wider text-primary">
          {item.stageName}
        </span>
      )}
    </div>
  );
}

/**
 * Renders a row's assignees, marking the current user's own name so a person
 * can spot their work without reading every name.
 * @param assignees - The row's assignees
 * @param currentUserId - The signed-in user's id, when known
 * @returns The assignee names
 */
function renderOwners(
  assignees: InboxItemAssignee[],
  currentUserId?: string,
): ReactNode {
  if (assignees.length === 0) {
    return <span className="text-muted-foreground">Unassigned</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-0.5">
      {assignees.map((assignee, index) => (
        <span key={assignee.userId}>
          {assignee.name}
          {assignee.userId === currentUserId && (
            <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
              you
            </span>
          )}
          {index < assignees.length - 1 ? "," : null}
        </span>
      ))}
    </span>
  );
}

/**
 * Renders what kind of task this is: an "Onboarding" pill (plus a UAT label)
 * for onboarding tasks, or a quiet uppercase micro-label naming the agent
 * action an agent task runs, or a general to-do when there is none.
 * @param row - The table row
 * @returns The task kind label
 */
function renderTaskKind(row: InboxTableRow): ReactNode {
  if (row.kind === "onboarding") {
    return (
      <span className="flex shrink-0 items-center gap-2">
        <DataTableBadge className="gap-1">
          <GraduationCap className="h-3 w-3" />
          Onboarding
        </DataTableBadge>
        {row.task.type === ONBOARDING_TASK_TYPE.UAT && (
          <span className={KIND_LABEL_CLASS}>UAT</span>
        )}
      </span>
    );
  }
  const label = row.item.actionParameters
    ? getTaskActionDisplayName(row.item.actionParameters.actionId)
    : "General";
  return <span className={KIND_LABEL_CLASS}>{label}</span>;
}

/**
 * Renders a row's status as a badge, pairing colour with a label and an icon
 * so the state never depends on colour alone.
 * @param row - The table row
 * @returns The status cell content
 */
function renderStatus(row: InboxTableRow): ReactNode {
  const { label, color, icon: Icon } = getRowStatusPresentation(row);
  return (
    <DataTableBadge color={color} className="gap-1">
      <Icon className="h-3 w-3" />
      {label}
    </DataTableBadge>
  );
}

/** How one task status reads in the table: its label, badge colour, and icon. */
interface StatusPresentation {
  label: string;
  color: "amber" | "emerald" | "slate" | "gray";
  icon: LucideIcon;
}

/**
 * Maps a row to its status presentation. Onboarding rows are always open
 * tasks, so they read as "Open".
 * @param row - The table row
 * @returns The label, badge colour, and icon for that row's status
 */
function getRowStatusPresentation(row: InboxTableRow): StatusPresentation {
  if (row.kind === "onboarding") {
    return { label: "Open", color: "amber", icon: Circle };
  }
  return getStatusPresentation(row.item.status);
}

/**
 * Maps a task status to its table presentation.
 * @param status - The task's status
 * @returns The label, badge colour, and icon for that status
 */
function getStatusPresentation(
  status: AgentWorkflowRunTaskStatus,
): StatusPresentation {
  switch (status) {
    case AgentWorkflowRunTaskStatus.PENDING_REVIEW:
      return { label: "Needs review", color: "amber", icon: Clock };
    case AgentWorkflowRunTaskStatus.APPROVED:
      return { label: "Done", color: "emerald", icon: Check };
    case AgentWorkflowRunTaskStatus.CANCELLED:
      return { label: "Cancelled", color: "slate", icon: X };
    case AgentWorkflowRunTaskStatus.PENDING:
      return { label: "Pending", color: "gray", icon: Circle };
  }
}

/**
 * Reads a row's title: the agent task's title, or the onboarding task's name.
 * @param row - The table row
 * @returns The row title
 */
function getRowTitle(row: InboxTableRow): string {
  return row.kind === "agent" ? getItemTitle(row.item) : row.task.name;
}

/**
 * Reads a row's assignees.
 * @param row - The table row
 * @returns The row's assignees, empty when unassigned
 */
function getRowAssignees(row: InboxTableRow): InboxItemAssignee[] {
  return row.kind === "agent" ? row.item.assignees : row.assignees;
}
